import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/studio";
  const prefillEmail = params.get("email") || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (prefillEmail) setEmail(prefillEmail);
  }, [prefillEmail]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await register(email, password, name);
      nav(next, { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4"
      style={{ background: "#f7f5f0" }}
      data-testid="register-page"
    >
      <div className="w-full max-w-md bg-white rounded-3xl shadow-sm border border-stone-200/80 p-8">
        <div className="flex items-baseline justify-between mb-1">
          <h1 className="font-display text-4xl text-stone-900">Atelier Studio</h1>
          <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
            Create account
          </span>
        </div>
        <p className="text-sm text-stone-500 mb-6">
          Start your <span className="text-stone-900 font-medium">28-day free trial</span>.
          No card required.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Name
            </label>
            <Input
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="register-name-input"
              placeholder="Your name"
              className="h-11"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Email
            </label>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email-input"
              placeholder="you@studio.com"
              className="h-11"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Password
            </label>
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password-input"
              placeholder="At least 8 characters"
              className="h-11"
            />
          </div>

          {err && (
            <div
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
              data-testid="register-error"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="register-submit-button"
            className="w-full h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                Creating account…
              </>
            ) : (
              "Create account"
            )}
          </Button>
        </form>

        <p className="text-xs text-stone-500 text-center mt-6">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-stone-900 underline underline-offset-2"
            data-testid="register-to-login-link"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
